/**
 * WebhookEventsPanel — Iter-48
 *
 * Super-admin panel showing recent RyftPay webhook events + manual refresh.
 * Mounted on /admin/dashboard as the "Webhooks" tab. Backed by
 * GET /api/admin/webhook-events + /api/admin/webhook-events/summary.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from '../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RefreshCw, Loader2, Zap, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';

const eventTypeVariant = (type) => {
  if (!type) return 'secondary';
  if (type.includes('failed') || type.includes('declined')) return 'destructive';
  if (type.includes('captured') || type.includes('completed')) return 'default';
  return 'secondary';
};

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
};

const EventRow = ({ event, index }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="border rounded-lg overflow-hidden"
      data-testid={`webhook-event-${index}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors text-left"
        data-testid={`webhook-event-toggle-${index}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {expanded ? (
            <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />
          )}
          <Badge variant={eventTypeVariant(event.event_type)} className="font-mono text-xs">
            {event.event_type || '(no type)'}
          </Badge>
          <span className="text-xs text-muted-foreground font-mono truncate">
            {event.event_id || event.id}
          </span>
        </div>
        <span className="text-xs text-muted-foreground shrink-0 ml-3">
          {formatTimestamp(event.received_at)}
        </span>
      </button>
      {expanded && (
        <pre
          className="text-xs bg-muted/40 p-3 border-t overflow-x-auto max-h-64"
          data-testid={`webhook-event-payload-${index}`}
        >
          {JSON.stringify(event.data, null, 2)}
        </pre>
      )}
    </div>
  );
};

export const WebhookEventsPanel = () => {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_type: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState('');

  const fetchData = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      try {
        const [eventsRes, summaryRes] = await Promise.all([
          apiGet('/api/admin/webhook-events', {
            query: eventTypeFilter ? { event_type: eventTypeFilter, limit: 50 } : { limit: 50 },
          }),
          apiGet('/api/admin/webhook-events/summary'),
        ]);
        setEvents(eventsRes || []);
        setSummary(summaryRes || { total: 0, by_type: [] });
      } catch (e) {
        toast.error(formatApiError(e, 'Failed to load webhook events'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [eventTypeFilter]
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventTypeFilter]);

  return (
    <div className="space-y-4" data-testid="webhook-events-panel">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" />
                RyftPay Webhook Events
              </CardTitle>
              <CardDescription>
                Real-time payment provider webhooks — last 30 days retained.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              data-testid="webhook-refresh-btn"
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary strip */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="webhook-summary">
            <Badge variant="outline" className="font-mono">
              Total (30d): {summary.total}
            </Badge>
            {summary.by_type.slice(0, 6).map((row) => (
              <Badge
                key={row.event_type}
                variant={eventTypeFilter === row.event_type ? 'default' : 'secondary'}
                className="cursor-pointer font-mono text-xs"
                onClick={() =>
                  setEventTypeFilter((cur) => (cur === row.event_type ? '' : row.event_type))
                }
                data-testid={`webhook-summary-${row.event_type}`}
              >
                {row.event_type} · {row.count}
              </Badge>
            ))}
          </div>

          {/* Filter input */}
          <div className="flex items-end gap-2">
            <div className="flex-1 max-w-xs">
              <Label htmlFor="webhook-event-type-filter" className="text-xs">
                Filter by event type
              </Label>
              <Input
                id="webhook-event-type-filter"
                placeholder="e.g. PaymentSession.captured"
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value)}
                data-testid="webhook-event-type-filter"
              />
            </div>
            {eventTypeFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEventTypeFilter('')}
                data-testid="webhook-clear-filter"
              >
                Clear
              </Button>
            )}
          </div>

          {/* Event list */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed rounded-lg">
              <Zap className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">
                No webhook events {eventTypeFilter ? `matching "${eventTypeFilter}"` : 'yet'}.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Events appear here as RyftPay posts payment/refund updates.
              </p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="webhook-events-list">
              {events.map((event, i) => (
                <EventRow key={event.id || event.event_id || i} event={event} index={i} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
